import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService, { Product as ApiProduct } from '../../services/api';

const DiscountsContainer = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: var(--color-secondary);
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: var(--color-accent);
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.secondary {
    background: var(--color-primary);
    color: white;
    
    &:hover {
      background: #6c757d;
    }
  }
  
  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  text-align: center;
  
  .value {
    font-size: 2rem;
    font-weight: bold;
    color: var(--color-accent);
    margin-bottom: 8px;
  }
  
  .label {
    color: var(--color-text-light);
    font-size: 0.9rem;
  }
`;

const DiscountForm = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-bottom: 30px;
`;

const FormTitle = styled.h3`
  color: var(--color-secondary);
  margin-bottom: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  
  label {
    font-weight: 500;
    margin-bottom: 8px;
    color: var(--color-secondary);
  }
  
  input, select, textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: var(--color-accent);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 60px;
  }
`;

const ProductSelection = styled.div`
  margin-bottom: 20px;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
`;

const ProductItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
  }
  
  input[type="checkbox"] {
    margin: 0;
  }
  
  .product-info {
    .name {
      font-weight: 500;
      font-size: 14px;
    }
    .price {
      color: var(--color-text-light);
      font-size: 12px;
    }
  }
`;

const DiscountedProductsList = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const ProductListHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProductListContent = styled.div`
  padding: 20px;
`;

const DiscountedProduct = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 15px;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
  
  .product-info {
    .name {
      font-weight: 500;
      margin-bottom: 4px;
    }
    .sku {
      color: var(--color-text-light);
      font-size: 0.8rem;
    }
  }
  
  .price-info {
    text-align: center;
    .original-price {
      text-decoration: line-through;
      color: var(--color-text-light);
      font-size: 0.9rem;
    }
    .final-price {
      font-weight: bold;
      color: var(--color-accent);
      font-size: 1.1rem;
    }
  }
  
  .discount-info {
    text-align: center;
    .discount-badge {
      background: #28a745;
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 500;
    }
    .discount-label {
      color: var(--color-text-light);
      font-size: 0.8rem;
      margin-top: 4px;
    }
  }
`;

type Product = ApiProduct;
type DiscountedProduct = ApiProduct;

const AdminDiscounts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [discountedProducts, setDiscountedProducts] = useState<DiscountedProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [discountLabel, setDiscountLabel] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsResponse, discountedResponse, statsResponse] = await Promise.all([
        apiService.getProducts({ includeAuth: true }),
        apiService.get('/discounts'),
        apiService.get('/discounts/statistics')
      ]);
      
      setProducts(productsResponse || []);
      setDiscountedProducts(discountedResponse || []);
      setStatistics(statsResponse);
    } catch (error) {
      console.error('Error loading discount data:', error);
    }
    setLoading(false);
  };

  const handleProductToggle = (productId: number) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id || 0).filter(id => id > 0));
    }
  };

  const handleApplyDiscount = async () => {
    if (selectedProducts.length === 0) {
      alert('Välj minst en produkt');
      return;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) {
      alert('Ange ett giltigt rabattvärde');
      return;
    }

    setLoading(true);
    try {
      const endpoint = selectedProducts.length === 1 ? '/discounts/set-single' : '/discounts/set-multiple';
      const payload: any = {
        discount_type: discountType,
        start_date: startDate || null,
        end_date: endDate || null,
        discount_label: discountLabel || null
      };

      if (discountType === 'percentage') {
        payload.discount_percentage = parseFloat(discountValue);
        payload.discount_amount = 0;
      } else {
        payload.discount_amount = parseFloat(discountValue);
        payload.discount_percentage = 0;
      }

      if (selectedProducts.length === 1) {
        payload.product_id = selectedProducts[0];
      } else {
        payload.product_ids = selectedProducts;
      }

      await apiService.post(endpoint, payload);
      
      // Reset form
      setSelectedProducts([]);
      setDiscountValue('');
      setStartDate('');
      setEndDate('');
      setDiscountLabel('');
      
      // Reload data
      loadData();
      
      alert(`Rabatt har satts för ${selectedProducts.length} produkter!`);
    } catch (error) {
      console.error('Error setting discount:', error);
      alert('Kunde inte sätta rabatt');
    }
    setLoading(false);
  };

  const handleRemoveDiscount = async (productIds: number[]) => {
    if (window.confirm(`Är du säker på att du vill ta bort rabatt från ${productIds.length} produkter?`)) {
      setLoading(true);
      try {
        await apiService.post('/discounts/remove', {
          product_ids: productIds
        });
        
        loadData();
        alert('Rabatter har tagits bort!');
      } catch (error) {
        console.error('Error removing discount:', error);
        alert('Kunde inte ta bort rabatt');
      }
      setLoading(false);
    }
  };

  if (loading && !products.length) {
    return (
      <DiscountsContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Laddar rabattdata...
        </div>
      </DiscountsContainer>
    );
  }

  return (
    <DiscountsContainer>
      <Header>
        <Title>Rabatter & Kampanjer</Title>
        <ActionButtons>
          <Button className="secondary" onClick={loadData}>
            🔄 Uppdatera
          </Button>
        </ActionButtons>
      </Header>

      {statistics && (
        <StatsGrid>
          <StatCard>
            <div className="value">{statistics.active_discounts}</div>
            <div className="label">Aktiva rabatter</div>
          </StatCard>
          <StatCard>
            <div className="value">{statistics.average_discount_percentage}%</div>
            <div className="label">Genomsnittlig rabatt</div>
          </StatCard>
          <StatCard>
            <div className="value">{statistics.expiring_soon}</div>
            <div className="label">Går ut inom 7 dagar</div>
          </StatCard>
        </StatsGrid>
      )}

      <DiscountForm>
        <FormTitle>Sätt rabatt på produkter</FormTitle>
        
        <FormGrid>
          <FormGroup>
            <label>Rabatttyp</label>
            <select 
              value={discountType} 
              onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}
            >
              <option value="percentage">Procent (%)</option>
              <option value="fixed">Fast belopp (kr)</option>
            </select>
          </FormGroup>
          
          <FormGroup>
            <label>Rabattvärde</label>
            <input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percentage' ? 't.ex. 20' : 't.ex. 50'}
              min="0"
              max={discountType === 'percentage' ? '100' : undefined}
            />
          </FormGroup>
          
          <FormGroup>
            <label>Startdatum (valfritt)</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FormGroup>
          
          <FormGroup>
            <label>Slutdatum (valfritt)</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormGroup>
        </FormGrid>

        <FormGroup style={{ marginBottom: '20px' }}>
          <label>Rabattetikett (visas för kunder)</label>
          <input
            type="text"
            value={discountLabel}
            onChange={(e) => setDiscountLabel(e.target.value)}
            placeholder="t.ex. 'Veckans erbjudande', 'Begränsad tid'"
            maxLength={100}
          />
        </FormGroup>

        <ProductSelection>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label style={{ fontWeight: '500', color: 'var(--color-secondary)' }}>
              Välj produkter ({selectedProducts.length} valda)
            </label>
            <Button className="secondary" onClick={handleSelectAll}>
              {selectedProducts.length === products.length ? 'Avmarkera alla' : 'Markera alla'}
            </Button>
          </div>
          
          <ProductGrid>
            {products.map(product => (
              <ProductItem key={product.id}>
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product.id || 0)}
                  onChange={() => handleProductToggle(product.id || 0)}
                />
                <div className="product-info">
                  <div className="name">{product.name}</div>
                  <div className="price">{product.price} kr • {product.sku || 'N/A'}</div>
                </div>
              </ProductItem>
            ))}
          </ProductGrid>
        </ProductSelection>

        <div style={{ textAlign: 'right' }}>
          <Button 
            className="primary" 
            onClick={handleApplyDiscount}
            disabled={loading || selectedProducts.length === 0}
          >
            Sätt rabatt på {selectedProducts.length} produkter
          </Button>
        </div>
      </DiscountForm>

      <DiscountedProductsList>
        <ProductListHeader>
          <h3 style={{ margin: 0 }}>Produkter med rabatt ({discountedProducts.length})</h3>
          {discountedProducts.length > 0 && (
            <Button 
              className="danger" 
              onClick={() => handleRemoveDiscount(discountedProducts.map(p => p.id || 0).filter(id => id > 0))}
            >
              Ta bort alla rabatter
            </Button>
          )}
        </ProductListHeader>
        
        <ProductListContent>
          {discountedProducts.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--color-text-light)', padding: '40px' }}>
              Inga produkter har rabatt för tillfället
            </div>
          ) : (
            discountedProducts.map(product => (
              <DiscountedProduct key={product.id}>
                <div className="product-info">
                  <div className="name">{product.name}</div>
                  <div className="sku">SKU: {product.sku || 'N/A'}</div>
                </div>
                
                <div className="price-info">
                  <div className="original-price">{product.price} kr</div>
                  <div className="final-price">{product.final_price || product.price} kr</div>
                </div>
                
                <div className="discount-info">
                  <div className="discount-badge">
                    {product.discount_type === 'percentage' 
                      ? `-${product.discount_percentage || 0}%`
                      : `-${product.discount_amount || 0} kr`
                    }
                  </div>
                  {product.discount_label && (
                    <div className="discount-label">{product.discount_label}</div>
                  )}
                </div>
                
                <Button 
                  className="danger" 
                  onClick={() => handleRemoveDiscount([product.id || 0])}
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Ta bort
                </Button>
              </DiscountedProduct>
            ))
          )}
        </ProductListContent>
      </DiscountedProductsList>
    </DiscountsContainer>
  );
};

export default AdminDiscounts;